<template>
  <div class="vp-page">
    <div class="vp-bg"><div class="vp-bg-overlay"></div></div>

    <div class="vp-container">

      <!-- Brand panel -->
      <div class="vp-brand">
        <div class="vp-brand-inner">
          <div class="vp-badge">Vendor Portal</div>
          <h2 class="vp-title">Access your vendor dashboard</h2>
          <p class="vp-sub">
            Manage your profile, respond to sourcing events, submit bids,
            and track purchase orders — all in one place.
          </p>
          <ul class="vp-perks">
            <li>Receive &amp; respond to RFQs / sourcing events</li>
            <li>Submit bids and track their status</li>
            <li>View purchase orders and transaction history</li>
            <li>Manage your documents and compliance records</li>
          </ul>
        </div>
      </div>

      <!-- Form card -->
      <div class="vp-card-wrap">
        <div class="vp-card">

          <div class="vp-card-head">
            <div class="vp-logo" v-if="companyCode">{{ companyCode }}</div>
            <h3 class="vp-form-title">Vendor Sign In</h3>
            <p class="vp-form-sub">Enter the credentials sent to your email.</p>
          </div>

          <div v-if="errorMsg" class="vp-error">
            <span>{{ errorMsg }}</span>
          </div>

          <form @submit.prevent="submit" novalidate>

            <div class="vp-field">
              <label>Email Address</label>
              <input
                v-model="email"
                type="email"
                placeholder="vendor@company.com"
                autocomplete="email"
                required
              />
            </div>

            <div class="vp-field">
              <label>Password</label>
              <div class="vp-pw">
                <input
                  v-model="password"
                  :type="show ? 'text' : 'password'"
                  placeholder="Enter your password"
                  autocomplete="current-password"
                  required
                />
                <button type="button" class="vp-eye" @click="show = !show" tabindex="-1">
                  {{ show ? '🙈' : '👁' }}
                </button>
              </div>
            </div>

            <button type="submit" class="vp-submit" :disabled="loading || !email || !password">
              {{ loading ? 'Signing in...' : 'Sign In' }}
            </button>

          </form>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';

export default {
  name: 'VendorLogin',

  setup() {
    const route  = useRoute();
    const router = useRouter();

    const companyCode = route.params.companyCode?.toUpperCase() ?? '';
    const email    = ref('');
    const password = ref('');
    const loading  = ref(false);
    const show     = ref(false);
    const errorMsg = ref('');

    const submit = async () => {
      if (!email.value || !password.value) return;
      loading.value = true;
      errorMsg.value = '';

      try {
        const { data } = await axios.post(`/vendor/${companyCode}/login`, {
          email:    email.value,
          password: password.value,
        });

        localStorage.setItem('vendor_token',        data.data.token);
        localStorage.setItem('vendor_company_code', companyCode);
        localStorage.setItem('vendor_data',         JSON.stringify(data.data.vendor));

        router.push(`/vendor/${companyCode}/dashboard`);
      } catch (err) {
        errorMsg.value = err.response?.data?.message ?? 'Login failed. Please check your credentials.';
      } finally {
        loading.value = false;
      }
    };

    return { companyCode, email, password, loading, show, errorMsg, submit };
  },
};
</script>

<style scoped>
.vp-page {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: stretch;
}

.vp-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: url('/static/img/banner-bg-5.jpg') center/cover no-repeat;
}
.vp-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(6,78,59,.96) 0%, rgba(5,150,105,.82) 100%);
}

.vp-container {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  padding: 40px 24px;
  gap: 48px;
  align-items: center;
  min-height: 100vh;
}

/* Brand panel */
.vp-brand { display: flex; align-items: center; }
.vp-brand-inner { display: flex; flex-direction: column; gap: 20px; }

.vp-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(255,255,255,.12);
  border: 1px solid rgba(255,255,255,.2);
  color: rgba(255,255,255,.92);
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  width: fit-content;
}

.vp-title { font-size: clamp(22px, 3vw, 30px); font-weight: 700; color: #fff; line-height: 1.35; margin: 0; }
.vp-sub   { font-size: 15px; color: rgba(255,255,255,.68); margin: 0; }

.vp-perks {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.vp-perks li {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255,255,255,.82);
  padding-left: 22px;
  position: relative;
}
.vp-perks li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #6ee7b7;
  font-weight: 700;
}

/* Card */
.vp-card-wrap { display: flex; align-items: center; }
.vp-card {
  background: #fff;
  border-radius: 16px;
  padding: 40px 36px;
  width: 100%;
  box-shadow: 0 24px 60px rgba(0,0,0,.22);
}

.vp-card-head { margin-bottom: 26px; }

.vp-logo {
  display: inline-block;
  background: #059669;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .1em;
  padding: 3px 10px;
  border-radius: 5px;
  margin-bottom: 12px;
}

.vp-form-title { font-size: 22px; font-weight: 800; color: #1a2b45; margin: 0 0 5px; }
.vp-form-sub   { font-size: 13.5px; color: #7a90b0; margin: 0; }

/* Error */
.vp-error {
  background: #fff5f5;
  border-left: 3px solid #e03535;
  border-radius: 0 8px 8px 0;
  padding: 10px 14px;
  margin-bottom: 18px;
  font-size: 13px;
  color: #c02828;
}

/* Fields */
.vp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
}
.vp-field label {
  font-size: 13px;
  font-weight: 600;
  color: #1a2b45;
}
.vp-field input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #dde5f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1a2b45;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  font-family: inherit;
  box-sizing: border-box;
}
.vp-field input:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,.09); }

.vp-pw { position: relative; display: flex; align-items: center; }
.vp-pw input { padding-right: 42px; }
.vp-eye {
  position: absolute;
  right: 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  line-height: 1;
}

/* Submit */
.vp-submit {
  width: 100%;
  padding: 13px;
  background: #059669;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, transform .1s;
  margin-top: 6px;
  font-family: inherit;
}
.vp-submit:hover:not(:disabled)  { background: #047857; }
.vp-submit:active:not(:disabled) { transform: scale(.98); }
.vp-submit:disabled { opacity: .5; cursor: not-allowed; }

/* Responsive */
@media (max-width: 860px) {
  .vp-container { grid-template-columns: 1fr; max-width: 480px; }
  .vp-brand     { display: none; }
}
@media (max-width: 480px) {
  .vp-card      { padding: 28px 20px; }
  .vp-container { padding: 24px 16px; }
}
</style>
